// src/components/GridView/renderers/GridTitleCell.tsx
import React, { useState, useCallback, useRef } from 'react';
import { useGridViewContext } from '../GridViewContext';
import ActionMenuConversation from '@components/ConversationItem/ActionMenuConversation';
import { convHasTitleEditPermissions } from '@lib/corgeeHelper';
import type Handsontable from 'handsontable/base';

// ==================== Types ====================

export interface GridTitleCellProps {
  /** Required by @handsontable/react to identify this as a renderer */
  'hot-renderer'?: boolean;
  /** Cell value (title string) — injected by Handsontable */
  value?: any;
  /** Visual row index — injected by Handsontable */
  row?: number;
  /** Visual column index — injected by Handsontable */
  col?: number;
  /** Column data property name — injected by Handsontable */
  prop?: string | number;
  /** The TD element — injected by Handsontable */
  TD?: HTMLTableCellElement;
  /** Cell properties — injected by Handsontable */
  cellProperties?: Handsontable.CellProperties;
  /** Handsontable instance — injected by Handsontable */
  hotInstance?: Handsontable;
}

// ==================== Component ====================

export function GridTitleCell({ row = 0, hotInstance, value }: GridTitleCellProps)
{
  const {
    conversations,
    onOpenInfoWindow,
    onRenameTitle,
    onRedFlagToggle,
    onImportantToggle,
    onDeleteConversation,
    onDuplicateConversation,
    actionMenuConfig,
  } = useGridViewContext();

  const [renaming, setRenaming] = useState(false);
  const [localTitle, setLocalTitle] = useState('');
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Map visual row → physical row (handles sorting)
  const physicalRow =
    hotInstance && typeof hotInstance.toPhysicalRow === 'function'
      ? (hotInstance.toPhysicalRow(row) ?? row)
      : row;

  const conversation = conversations[physicalRow];

  const commitRename = useCallback(() =>
  {
    if (!conversation) return;
    const trimmed = localTitle.trim();
    if (trimmed && trimmed !== conversation.Title && onRenameTitle)
    {
      onRenameTitle(conversation.ConversationId, trimmed);
    }
    setRenaming(false);
  }, [conversation, localTitle, onRenameTitle]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) =>
    {
      if (e.key === 'Enter')
      {
        e.preventDefault();
        commitRename();
      }
      else if (e.key === 'Escape')
      {
        e.preventDefault();
        setRenaming(false);
      }
    },
    [commitRename],
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) =>
    {
      const target = e.target as HTMLElement;
      if (target.closest('.gridview-title-kebab')) return;
      // Cancel any pending single-click (InfoWindow open)
      if (clickTimerRef.current)
      {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      if (!conversation) return;
      if (!convHasTitleEditPermissions(conversation)) return;
      setLocalTitle(conversation.Title ?? '');
      setRenaming(true);
    },
    [conversation],
  );

  const handleCellClick = useCallback(
    (e: React.MouseEvent) =>
    {
      const target = e.target as HTMLElement;
      if (target.closest('.gridview-title-kebab')) return;
      if (renaming) return;

      // Delay InfoWindow open so double-click can cancel it
      clickTimerRef.current = setTimeout(() =>
      {
        clickTimerRef.current = null;
        if (conversation && onOpenInfoWindow)
        {
          onOpenInfoWindow(conversation);
        }
      }, 200);
    },
    [conversation, onOpenInfoWindow, renaming],
  );

  // Row not yet in context (e.g. during initial HOT render before context propagates)
  if (!conversation)
  {
    return <span className="gridview-title-text">{value ?? ''}</span>;
  }

  const layers = conversation.ConversationLayers ?? [];
  // Deduplicate by LayerId — the backend can return the same layer more than once
  const uniqueLayers = Array.from(new Map(layers.map(l => [l.LayerId, l])).values());
  const visibleLayers = uniqueLayers.slice(0, 3);

  // ── Rename mode ──
  if (renaming)
  {
    return (
      <div className="gridview-title-cell">
        <input
          className="gridview-title-rename-input"
          value={localTitle}
          onChange={e => setLocalTitle(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={commitRename}
          autoFocus
          onClick={e => e.stopPropagation()}
        />
      </div>
    );
  }

  return (
    <div className="gridview-title-cell">
      {/* Card icon — Icon is a CSS class name from Corgee's icons-menu font (e.g. "icons-menu-tasks").
           Rendered via ::before pseudo-element; tinted with the primary layer color. */}

      {conversation.Icon && (
        <span className={`gridview-title-icon ${conversation.Icon}`} aria-hidden="true" />
      )}

      {/* Title text — click opens InfoWindow, double-click enters rename mode */}
      <div className="gridview-title-text-container">
        <span
          className={`gridview-title-text ${conversation.IsRedFlag ? 'gridview-title-red' : ''}`}
          title={
            convHasTitleEditPermissions(conversation)
              ? `${conversation.Title} — Double click to rename`
              : conversation.Title
          }
          onClick={handleCellClick}
          onDoubleClick={handleDoubleClick}
          style={{ cursor: 'pointer' }}
        >
          {conversation.Title}
        </span>
      </div>

      {/* Important star */}
      {conversation.IsImportant && (
        <span className="gridview-title-important" title="Important" aria-hidden="true"></span>
      )}

      {/* Layer color dots */}
      {visibleLayers.length > 0 && (
        <div className="gridview-title-layers" aria-hidden="true">
          {visibleLayers.map(layer => (
            <span
              key={layer.LayerId}
              className="gridview-title-layer-dot"
              style={{ backgroundColor: layer.LayerColor || (layer as any).Color || '' }}
              title={layer.LayerName}
            />
          ))}
        </div>
      )}

      {/* Action menu — uses ActionMenuConversation (same as CalendarView).
          Its dropdown renders via RenderInBody with position:fixed so it
          escapes Handsontable's stacking context automatically. */}
      {actionMenuConfig && (
        <div className="gridview-title-kebab">
          {/* ActionMenuConversation manages its own 3-dot trigger + dropdown (position:fixed via RenderInBody) */}
          {React.createElement(ActionMenuConversation as any, {
            index: row,
            conversation: conversation as any,
            api: actionMenuConfig.api,
            config: actionMenuConfig.config,
            storageData: actionMenuConfig.storageData,
            configMenus: actionMenuConfig.configMenus,
            realItemTypes: actionMenuConfig.realItemTypes,
            userData: actionMenuConfig.userData,
            onMarkAsRedFlag: (_e: React.MouseEvent) =>
            {
              const newVal = !conversation.IsRedFlag;
              onRedFlagToggle?.(conversation.ConversationId, newVal);
            },
            onMarkAsImportant: (_e: React.MouseEvent) =>
            {
              const newVal = conversation.IsImportant ? null : true;
              onImportantToggle?.(conversation.ConversationId, newVal);
            },
            HasItemCreationPermission: localStorage.getItem('C2_hasItemCreationPermission') === 'true',
            corgeeFunctions: {
              openInfoWindow: (conv: any) => onOpenInfoWindow?.(conv),
              deleteConversation: (_e: any, conv: any) =>
              {
                onDeleteConversation?.(conv.ConversationId);
              },
              onDuplicateItem: (e: any, conv: any) =>
              {
                onDuplicateConversation?.(e, conv);
              },
            },
          })}
        </div>
      )}
    </div>
  );
}
